import { useMemo, useRef, useEffect } from 'react'
import { useAuth } from '@/frontend/contexts/AuthContext'
import { useCompany } from '@/frontend/contexts/CompanyContext'
import { createAuthenticatedApiClient } from '@/frontend/lib/honoApiClient'

/**
 * Hook that provides an authenticated API client using Firebase ID tokens
 * This ensures all API requests include the user's authentication token and company ID
 * Automatically handles token refresh on 401 responses
 */
export function useAuthenticatedApi() {
	const { user, getToken } = useAuth()
	const { selectedCompany } = useCompany()
	
	// Use a ref to always get the current company value
	const selectedCompanyRef = useRef(selectedCompany)
	
	// Update the ref whenever selectedCompany changes
	useEffect(() => {
		selectedCompanyRef.current = selectedCompany
	}, [selectedCompany])

	const apiClient = useMemo(() => {
		return createAuthenticatedApiClient(
			async (retryAttempt = 0) => {
				try {
					if (!user) {
						console.warn('[API] No authenticated user available')
						return null
					}

					// Force refresh token if this is a retry attempt
					const forceRefresh = retryAttempt > 0
					const token = await getToken(forceRefresh)
					
					if (forceRefresh) {
						console.log('[API] Retrieved refreshed Firebase ID token:', {
							hasToken: !!token,
							tokenLength: token?.length || 0,
							userId: user.uid,
							retryAttempt,
							timestamp: new Date().toISOString()
						})
					}
					
					return token
				} catch (error) {
					console.error('[API] Failed to get Firebase ID token:', {
						error,
						retryAttempt,
						userId: user?.uid
					})
					return null
				}
			},
			() => {
				// Always get the current company value from the ref
				const company = selectedCompanyRef.current?.company || null
				return company
			}
		)
	}, [user, getToken]) // Remove selectedCompany from deps since we're using ref

	return apiClient
}
